import type {
  DrawnCard,
  Element,
  Orientation,
  QabalisticWorld,
  Suit,
  TarotCard,
} from '@lilith-arcana-os/domain';
import { getSpreadById } from '@lilith-arcana-os/spread-registry/derived/slugs';

export const DEFAULT_TAROT_DECK_SIZE = 78;

const SUITS: Suit[] = ['Bastoni', 'Coppe', 'Spade', 'Denari'];
const ELEMENT_BY_SUIT: Record<Suit, Element> = {
  Bastoni: 'Fuoco',
  Coppe: 'Acqua',
  Spade: 'Aria',
  Denari: 'Terra',
};
const WORLDS: QabalisticWorld[] = ['Atziluth', 'Briah', 'Yetzirah', 'Assiah'];
const COURT_RANKS = ['Fante', 'Cavaliere', 'Regina', 'Re'] as const;
const MAJOR_ELEMENTS: Element[] = ['Fuoco', 'Acqua', 'Aria', 'Terra'];

const normalizeCardId = (cardId: number): number =>
  Number.isInteger(cardId) && cardId > 0 ? cardId : 1;

const resolveMinorIndex = (cardId: number): number => {
  const normalized = normalizeCardId(cardId);
  return ((normalized - 23) % 56 + 56) % 56;
};

export function buildBootstrapDeck(deckSize = DEFAULT_TAROT_DECK_SIZE): number[] {
  const normalizedDeckSize =
    Number.isInteger(deckSize) && deckSize > 0 ? Math.min(deckSize, DEFAULT_TAROT_DECK_SIZE) : DEFAULT_TAROT_DECK_SIZE;

  return Array.from({ length: normalizedDeckSize }, (_, index) => index + 1);
}

export function resolveBootstrapSpread(spreadId: string) {
  const spread = getSpreadById(spreadId === 'three-card' ? 'past-present-future' : spreadId);

  if (!spread) {
    throw new Error(`Unknown registered spread: ${spreadId}`);
  }

  return spread;
}

export function buildStubTarotCard(cardId: number): TarotCard {
  const normalizedCardId = normalizeCardId(cardId);
  const isMajorArcana = normalizedCardId <= 22;

  if (isMajorArcana) {
    const element = MAJOR_ELEMENTS[(normalizedCardId - 1) % MAJOR_ELEMENTS.length] ?? 'Fuoco';

    return {
      id: normalizedCardId,
      name: `Major Placeholder ${normalizedCardId}`,
      slug: `major-placeholder-${normalizedCardId}`,
      arcana: 'major',
      hermetic_title: `Major current ${normalizedCardId}`,
      suit: null,
      rank: null,
      number: normalizedCardId,
      court: false,
      element,
      element_secondary: null,
      qabalistic_world: null,
      sephirah: null,
      path_number: null,
      hebrew_letter: null,
      hebrew_char: null,
      tree_position: null,
      qliphah: null,
      tunnel_of_set: null,
      qliphotic_meaning: 'Bootstrap shadow metadata pending.',
      astro_primary: `Placeholder astrology ${normalizedCardId}`,
      zodiac_sign: null,
      zodiac_modality: null,
      decan: null,
      chaldean_mirror_card_id: null,
      angels_day: null,
      angels_night: null,
      goetia_shadow: null,
      gemstone: null,
      incense: null,
      color_primary: '#c9a96e',
      body_part: null,
      musical_note: null,
      keywords_up: [`Signal ${normalizedCardId}`, 'Threshold'],
      keywords_rev: [`Shadow ${normalizedCardId}`, 'Inversion'],
      meaning_up: `Placeholder upright meaning for card ${normalizedCardId}.`,
      meaning_rev: `Placeholder reversed meaning for card ${normalizedCardId}.`,
      meaning_love: `Placeholder love current for card ${normalizedCardId}.`,
      meaning_work: `Placeholder work current for card ${normalizedCardId}.`,
      meaning_spirit: `Placeholder spiritual current for card ${normalizedCardId}.`,
      meaning_psyche: `Placeholder psyche current for card ${normalizedCardId}.`,
      lilith_voice: `Placeholder Lilith voice for card ${normalizedCardId}.`,
      lilith_qliphah_link: null,
      simbologia: `Bootstrap symbolic payload for card ${normalizedCardId}.`,
      symbol_emoji: '*',
      numerology_value: normalizedCardId,
      numerology_meaning: `Placeholder numerology for ${normalizedCardId}.`,
      dignity: null,
      solar_dates: null,
    };
  }

  const minorIndex = resolveMinorIndex(normalizedCardId);
  const suit = SUITS[Math.floor(minorIndex / 14)] ?? 'Bastoni';
  const rankIndex = minorIndex % 14;
  const court = rankIndex >= 10;
  const rank = court ? COURT_RANKS[rankIndex - 10] ?? 'Fante' : null;
  const number = rankIndex + 1;

  return {
    id: normalizedCardId,
    name: court ? `${rank} di ${suit}` : `${number} di ${suit}`,
    slug: `minor-placeholder-${normalizedCardId}`,
    arcana: 'minor',
    hermetic_title: `Minor current ${normalizedCardId}`,
    suit,
    rank,
    number,
    court,
    element: ELEMENT_BY_SUIT[suit],
    element_secondary: null,
    qabalistic_world: WORLDS[Math.floor(minorIndex / 14)] ?? 'Atziluth',
    sephirah: null,
    path_number: null,
    hebrew_letter: null,
    hebrew_char: null,
    tree_position: null,
    qliphah: null,
    tunnel_of_set: null,
    qliphotic_meaning: 'Bootstrap shadow metadata pending.',
    astro_primary: `Placeholder astrology ${normalizedCardId}`,
    zodiac_sign: null,
    zodiac_modality: null,
    decan: null,
    chaldean_mirror_card_id: null,
    angels_day: null,
    angels_night: null,
    goetia_shadow: null,
    gemstone: null,
    incense: null,
    color_primary: '#800040',
    body_part: null,
    musical_note: null,
    keywords_up: [`Current ${normalizedCardId}`, suit],
    keywords_rev: [`Shadow ${normalizedCardId}`, suit],
    meaning_up: `Placeholder upright meaning for card ${normalizedCardId}.`,
    meaning_rev: `Placeholder reversed meaning for card ${normalizedCardId}.`,
    meaning_love: `Placeholder love current for card ${normalizedCardId}.`,
    meaning_work: `Placeholder work current for card ${normalizedCardId}.`,
    meaning_spirit: `Placeholder spiritual current for card ${normalizedCardId}.`,
    meaning_psyche: `Placeholder psyche current for card ${normalizedCardId}.`,
    lilith_voice: `Placeholder Lilith voice for card ${normalizedCardId}.`,
    lilith_qliphah_link: null,
    simbologia: `Bootstrap symbolic payload for card ${normalizedCardId}.`,
    symbol_emoji: '*',
    numerology_value: number,
    numerology_meaning: `Placeholder numerology for ${normalizedCardId}.`,
    dignity: null,
    solar_dates: null,
  };
}

export function buildDrawnCards(input: {
  cardIds: number[];
  orientations: Orientation[];
  spreadId: string;
}): DrawnCard[] {
  const spread = resolveBootstrapSpread(input.spreadId);

  return input.cardIds.map<DrawnCard>((cardId, index) => ({
    ...buildStubTarotCard(cardId),
    orientation: input.orientations[index] ?? 'upright',
    position: spread.positions[index] ?? {
      index,
      name: `Position ${index + 1}`,
      description: 'Bootstrap position placeholder.',
      sephirah_correspondence: null,
      qliphah_correspondence: null,
      element_affinity: null,
      x: index,
      y: 0,
      z: 0,
    },
    draw_index: index,
  }));
}
